import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  listUsers, inviteUser, userAction, PortalUser, UserAction,
  listMcpAccess, putGlobalMcpPolicy,
  McpId, MCP_IDS, McpPolicyEntry,
} from '../api';
import { useAuth } from '../components/AuthProvider';
import McpAccessEditor, { EditorSubject } from '../components/McpAccessEditor';

// ── Engine-mirroring helpers ─────────────────────────────────────────────────

function hasPolicyFields(e: McpPolicyEntry | null | undefined): boolean {
  return !!e && (e.allowed !== undefined || e.toolRules !== undefined || e.domainRules !== undefined);
}

interface CellState {
  override: McpPolicyEntry | null;
  stampOnly: McpPolicyEntry | null;
  global: McpPolicyEntry | null;
}

function effectiveAllowed(cell: CellState): boolean {
  const base = hasPolicyFields(cell.override)
    ? cell.override
    : hasPolicyFields(cell.global)
      ? cell.global
      : null;
  if (!base) return false; // fail closed
  return base.allowed !== false;
}

// ── Status colors ────────────────────────────────────────────────────────────

const statusColors: Record<string, React.CSSProperties> = {
  CONFIRMED: { background: '#d4edda', color: '#155724' },
  FORCE_CHANGE_PASSWORD: { background: '#fff3cd', color: '#856404' },
  EXTERNAL_PROVIDER: { background: '#d1ecf1', color: '#0c5460' },
};

// ── Component ────────────────────────────────────────────────────────────────

interface EditTarget {
  subject: EditorSubject;
  mcp: McpId;
  entry: McpPolicyEntry | null;
}

const AdminUsers: React.FC = () => {
  const { user: me } = useAuth();
  const [users, setUsers] = useState<PortalUser[]>([]);
  const [policies, setPolicies] = useState<McpPolicyEntry[]>([]);
  const [protectedTools, setProtectedTools] = useState<Partial<Record<McpId, string[]>>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviting, setInviting] = useState(false);
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());
  const [editTarget, setEditTarget] = useState<EditTarget | null>(null);

  const load = useCallback(async () => {
    try {
      const [u, access] = await Promise.all([listUsers(), listMcpAccess()]);
      setUsers(u);
      setPolicies(access.policies);
      setProtectedTools(access.protectedTools ?? {});
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const byId = useMemo(() => new Map(policies.map(p => [p.policyId, p])), [policies]);

  const cell = useCallback((sub: string, mcp: McpId): CellState => {
    const userItem = byId.get(`user#${sub}#${mcp}`) ?? null;
    return {
      override: hasPolicyFields(userItem) ? userItem : null,
      stampOnly: userItem && !hasPolicyFields(userItem) ? userItem : null,
      global: byId.get(`global#${mcp}`) ?? null,
    };
  }, [byId]);

  const toggleExpand = (username: string) => {
    setExpandedUsers(prev => {
      const next = new Set(prev);
      if (next.has(username)) next.delete(username);
      else next.add(username);
      return next;
    });
  };

  const onInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail || !inviteName) return;
    setInviting(true);
    setError('');
    setNotice('');
    try {
      await inviteUser(inviteEmail.trim(), inviteName.trim());
      setNotice(`Invitation sent to ${inviteEmail.trim()}.`);
      setInviteEmail('');
      setInviteName('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to invite user.');
    } finally {
      setInviting(false);
    }
  };

  const onAction = async (u: PortalUser, action: UserAction, confirmText: string) => {
    if (!window.confirm(confirmText)) return;
    setBusy(u.username);
    setError('');
    setNotice('');
    try {
      await userAction(u.username, action);
      setNotice(`${action} succeeded for ${u.email}.`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${action}.`);
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return <p style={styles.loading}>Loading users...</p>;
  }

  return (
    <div>
      <div style={styles.header}>
        <h1 style={styles.heading}>Users</h1>
      </div>

      {error && <div style={styles.errorBanner}>{error}</div>}
      {notice && <div style={styles.noticeBanner}>{notice}</div>}

      {/* ── MCP defaults card ─────────────────────────────────────────── */}
      <div style={styles.defaultsCard}>
        <p style={styles.defaultsHeading}>MCP defaults for new users</p>
        <div style={styles.defaultsRow}>
          {MCP_IDS.map(mcp => {
            const g = byId.get(`global#${mcp}`) ?? null;
            const allowed = hasPolicyFields(g) ? g!.allowed !== false : false;
            return (
              <div key={mcp} style={styles.defaultsChip}>
                <span style={styles.defaultsMcpName}>{mcp}</span>
                <button
                  style={allowed ? styles.chipAllowed : styles.chipDenied}
                  onClick={async () => {
                    setError(''); setNotice('');
                    try {
                      await putGlobalMcpPolicy(mcp, {
                        allowed: !allowed,
                        toolRules: g?.toolRules,
                        domainRules: g?.domainRules,
                      });
                      setNotice(`Global ${mcp} default set to ${!allowed ? 'Allowed' : 'Denied'}.`);
                      await load();
                    } catch (err) {
                      setError(err instanceof Error ? err.message : 'Update failed.');
                    }
                  }}
                >
                  {allowed ? 'Allowed' : 'Denied'}
                </button>
                <button
                  style={styles.editLink}
                  onClick={() => setEditTarget({
                    subject: { kind: 'global' },
                    mcp,
                    entry: g,
                  })}
                >
                  edit
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Invite form ───────────────────────────────────────────────── */}
      <form onSubmit={onInvite} style={styles.inviteForm}>
        <input
          type="email"
          placeholder="email@di.net.au"
          value={inviteEmail}
          onChange={e => setInviteEmail(e.target.value)}
          style={styles.input}
          required
        />
        <input
          type="text"
          placeholder="Full name"
          value={inviteName}
          onChange={e => setInviteName(e.target.value)}
          style={styles.input}
          required
        />
        <button type="submit" disabled={inviting} style={styles.inviteBtn}>
          {inviting ? 'Inviting...' : '+ Invite User'}
        </button>
      </form>

      {/* ── User table ────────────────────────────────────────────────── */}
      {users.length === 0 ? (
        <div style={styles.empty}><p>No users found.</p></div>
      ) : (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.th, width: 36 }}></th>
                <th style={styles.th}>Email</th>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>MFA</th>
                <th style={styles.th}>Admin</th>
                <th style={styles.th}>Last Activity</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => {
                const isSelf = u.email.toLowerCase() === (me?.email ?? '').toLowerCase();
                const isInvitePending = u.status === 'FORCE_CHANGE_PASSWORD';
                const disabled = busy === u.username;
                const expanded = expandedUsers.has(u.username);

                return (
                  <React.Fragment key={u.username}>
                    <tr style={{ ...styles.tr, opacity: u.enabled ? 1 : 0.5 }}>
                      <td style={{ ...styles.td, textAlign: 'center', padding: '0.75rem 0.4rem' }}>
                        <button
                          style={styles.chevronBtn}
                          onClick={() => toggleExpand(u.username)}
                          title={expanded ? 'Collapse MCP access' : 'Expand MCP access'}
                        >
                          {expanded ? 'v' : '>'}
                        </button>
                      </td>
                      <td style={styles.td}>
                        {u.email}
                        {isSelf && <span style={styles.youBadge}>you</span>}
                      </td>
                      <td style={styles.td}>{u.name || '--'}</td>
                      <td style={styles.td}>
                        <span
                          style={{
                            ...styles.badge,
                            ...(u.enabled
                              ? statusColors[u.status] ?? {}
                              : { background: '#f8d7da', color: '#721c24' }),
                          }}
                        >
                          {u.enabled ? u.status : 'DISABLED'}
                        </span>
                      </td>
                      <td style={styles.td}>{u.mfaEnabled ? 'Enrolled' : '--'}</td>
                      <td style={styles.td}>{u.groups.includes('admins') ? 'Admin' : '--'}</td>
                      <td style={styles.td}>
                        {u.lastModifiedAt ? new Date(u.lastModifiedAt).toLocaleDateString() : '--'}
                      </td>
                      <td style={styles.td}>
                        <div style={styles.actionRow}>
                          {u.enabled ? (
                            !isSelf && (
                              <button
                                style={styles.dangerBtn}
                                disabled={disabled}
                                onClick={() => onAction(u, 'disable', `Disable ${u.email}? They will not be able to sign in.`)}
                              >
                                Disable
                              </button>
                            )
                          ) : (
                            <button
                              style={styles.actionBtn}
                              disabled={disabled}
                              onClick={() => onAction(u, 'enable', `Re-enable ${u.email}?`)}
                            >
                              Enable
                            </button>
                          )}
                          {isInvitePending ? (
                            <button
                              style={styles.actionBtn}
                              disabled={disabled}
                              onClick={() => onAction(u, 'resend-invite', `Re-send the invitation email (new temporary password) to ${u.email}?`)}
                            >
                              Resend Invite
                            </button>
                          ) : (
                            <button
                              style={styles.actionBtn}
                              disabled={disabled}
                              onClick={() => onAction(u, 'reset-password', `Send a password-reset code to ${u.email}?`)}
                            >
                              Reset Password
                            </button>
                          )}
                          {u.groups.includes('admins') ? (
                            !isSelf && (
                              <button
                                style={styles.dangerBtn}
                                disabled={disabled}
                                onClick={() => onAction(u, 'remove-admin', `Remove admin rights from ${u.email}?`)}
                              >
                                Remove Admin
                              </button>
                            )
                          ) : (
                            <button
                              style={styles.actionBtn}
                              disabled={disabled}
                              onClick={() => onAction(u, 'add-admin', `Grant admin rights to ${u.email}?`)}
                            >
                              Make Admin
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* ── MCP expander row ── */}
                    {expanded && (
                      <tr key={`${u.username}__mcp`} style={styles.expanderRow}>
                        <td colSpan={8} style={styles.expanderCell}>
                          <div style={styles.mcpCardsRow}>
                            {MCP_IDS.map(mcp => {
                              // Policy is keyed by Cognito sub (the MCP servers use the JWT sub),
                              // NOT username — they differ for federated (Google_*) users.
                              const c = cell(u.sub, mcp);
                              const allowed = effectiveAllowed(c);
                              const lastUsed = c.override?.lastUsed ?? c.stampOnly?.lastUsed;
                              const hasOverride = !!c.override;
                              return (
                                <div key={mcp} style={styles.mcpCard}>
                                  <div style={styles.mcpCardHeader}>
                                    <span style={styles.mcpCardName}>{mcp}</span>
                                    <span style={allowed ? styles.statusAllowed : styles.statusDenied}>
                                      {allowed ? 'Allowed' : 'Denied'}
                                    </span>
                                  </div>
                                  <div style={styles.mcpCardMeta}>
                                    <span style={hasOverride ? styles.overrideBadge : styles.inheritsBadge}>
                                      {hasOverride ? 'override' : 'inherits global'}
                                    </span>
                                    <span style={styles.lastUsedText}>
                                      {lastUsed
                                        ? `last used ${new Date(lastUsed).toLocaleDateString()}`
                                        : 'never used'}
                                    </span>
                                  </div>
                                  <button
                                    style={styles.editAccessBtn}
                                    onClick={() => setEditTarget({
                                      subject: { kind: 'user', sub: u.sub, email: u.email },
                                      mcp,
                                      entry: c.override ?? c.stampOnly,
                                    })}
                                  >
                                    Edit access
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Editor modal ──────────────────────────────────────────────── */}
      {editTarget && (
        <McpAccessEditor
          subject={editTarget.subject}
          mcp={editTarget.mcp}
          entry={editTarget.entry}
          protectedTools={protectedTools}
          onClose={() => setEditTarget(null)}
          onSaved={msg => {
            setEditTarget(null);
            setNotice(msg);
            load();
          }}
        />
      )}
    </div>
  );
};

// ── Styles ───────────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' },
  heading: { fontSize: '1.6rem', fontWeight: 700 },
  loading: { textAlign: 'center', padding: '3rem', color: '#888' },
  errorBanner: {
    background: '#f8d7da', color: '#721c24', border: '1px solid #f5c6cb',
    borderRadius: 6, padding: '0.85rem 1rem', marginBottom: '1rem', fontSize: '0.9rem',
  },
  noticeBanner: {
    background: '#d4edda', color: '#155724', border: '1px solid #c3e6cb',
    borderRadius: 6, padding: '0.85rem 1rem', marginBottom: '1rem', fontSize: '0.9rem',
  },

  // Defaults card
  defaultsCard: {
    background: '#fafafa', border: '1px solid #e8e8e8',
    borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem',
  },
  defaultsHeading: {
    fontSize: '0.75rem', fontWeight: 600, color: '#888',
    textTransform: 'uppercase' as const, letterSpacing: '0.05em', marginBottom: '0.5rem',
  },
  defaultsRow: { display: 'flex', gap: '1.5rem', flexWrap: 'wrap' as const },
  defaultsChip: { display: 'flex', alignItems: 'center', gap: '0.4rem' },
  defaultsMcpName: { fontSize: '0.85rem', fontWeight: 600, color: '#333', fontFamily: 'monospace' },
  chipAllowed: {
    background: '#d4edda', color: '#155724', border: '1px solid #c3e6cb',
    padding: '0.2rem 0.5rem', borderRadius: 4, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
  },
  chipDenied: {
    background: '#f8d7da', color: '#721c24', border: '1px solid #f5c6cb',
    padding: '0.2rem 0.5rem', borderRadius: 4, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
  },
  editLink: {
    background: 'none', border: 'none', color: '#4a6cf7',
    fontSize: '0.78rem', cursor: 'pointer', textDecoration: 'underline', padding: 0,
  },

  // Invite form
  inviteForm: {
    display: 'flex', gap: '0.75rem', marginBottom: '1.5rem',
    background: '#fff', borderRadius: 8, padding: '1rem',
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
  },
  input: { flex: 1, padding: '0.5rem 0.75rem', borderRadius: 6, border: '1px solid #ccc', fontSize: '0.9rem' },
  inviteBtn: {
    background: '#4a6cf7', color: '#fff', border: 'none',
    padding: '0.5rem 1.25rem', borderRadius: 6, fontSize: '0.9rem',
    fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' as const,
  },

  // Table
  empty: {
    textAlign: 'center', padding: '3rem', background: '#fff',
    borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
  },
  tableWrapper: {
    overflowX: 'auto', background: '#fff',
    borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    textAlign: 'left', padding: '0.75rem 1rem', fontSize: '0.8rem',
    fontWeight: 600, color: '#666', textTransform: 'uppercase' as const,
    letterSpacing: '0.04em', borderBottom: '2px solid #eee',
  },
  tr: { borderBottom: '1px solid #f0f0f0' },
  td: { padding: '0.75rem 1rem', fontSize: '0.9rem', verticalAlign: 'top' },
  badge: { display: 'inline-block', padding: '0.2rem 0.6rem', borderRadius: 12, fontSize: '0.78rem', fontWeight: 600 },
  youBadge: {
    marginLeft: '0.5rem', fontSize: '0.7rem', fontWeight: 700, color: '#4a6cf7',
    border: '1px solid #4a6cf7', borderRadius: 10, padding: '0.05rem 0.4rem',
    textTransform: 'uppercase' as const,
  },
  actionRow: { display: 'flex', gap: '0.4rem', flexWrap: 'wrap' as const },
  actionBtn: {
    background: 'transparent', border: '1px solid #4a6cf7', color: '#4a6cf7',
    padding: '0.25rem 0.6rem', borderRadius: 4, fontSize: '0.78rem',
    fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' as const,
  },
  dangerBtn: {
    background: 'transparent', border: '1px solid #dc3545', color: '#dc3545',
    padding: '0.25rem 0.6rem', borderRadius: 4, fontSize: '0.78rem',
    fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' as const,
  },
  chevronBtn: {
    background: 'none', border: 'none', color: '#666',
    cursor: 'pointer', fontSize: '0.9rem', padding: '0.15rem 0.3rem', borderRadius: 3,
  },

  // Expander
  expanderRow: { background: '#f7f9fc' },
  expanderCell: { padding: '1rem 1.25rem', borderBottom: '2px solid #e8edf5' },
  mcpCardsRow: { display: 'flex', gap: '1rem', flexWrap: 'wrap' as const },
  mcpCard: {
    flex: '1 1 180px', background: '#fff',
    border: '1px solid #dde3ed', borderRadius: 6,
    padding: '0.75rem 1rem', minWidth: 180,
  },
  mcpCardHeader: {
    display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', marginBottom: '0.4rem',
  },
  mcpCardName: { fontSize: '0.85rem', fontWeight: 700, fontFamily: 'monospace', color: '#222' },
  statusAllowed: {
    background: '#d4edda', color: '#155724', border: '1px solid #c3e6cb',
    padding: '0.15rem 0.45rem', borderRadius: 4, fontSize: '0.72rem', fontWeight: 600,
  },
  statusDenied: {
    background: '#f8d7da', color: '#721c24', border: '1px solid #f5c6cb',
    padding: '0.15rem 0.45rem', borderRadius: 4, fontSize: '0.72rem', fontWeight: 600,
  },
  mcpCardMeta: {
    display: 'flex', alignItems: 'center', gap: '0.5rem',
    marginBottom: '0.6rem', flexWrap: 'wrap' as const,
  },
  overrideBadge: {
    fontSize: '0.68rem', fontWeight: 700, color: '#856404',
    background: '#fff3cd', borderRadius: 10, padding: '0.05rem 0.4rem',
  },
  inheritsBadge: {
    fontSize: '0.68rem', fontWeight: 700, color: '#555',
    background: '#eee', borderRadius: 10, padding: '0.05rem 0.4rem',
  },
  lastUsedText: { fontSize: '0.72rem', color: '#999' },
  editAccessBtn: {
    background: 'transparent', border: '1px solid #4a6cf7', color: '#4a6cf7',
    padding: '0.25rem 0.6rem', borderRadius: 4, fontSize: '0.78rem',
    fontWeight: 600, cursor: 'pointer', width: '100%',
  },
};

export default AdminUsers;
